import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/modules/auth/stores/auth";
import { useImpersonationStore } from "@/modules/auth/stores/impersonation";
import { ROUTE_NAME } from "@/shared/constants/routes";
import { getDefaultLandingRouteName } from "@/shared/utils/defaultLandingRoute";

import { LOGIN_MAGIC_LINK_PATH } from "@/shared/constants/routes";

import SentryView from "@/views/SentryView.vue";

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    // always scroll to top
    return { top: 0 };
  },
  routes: [
    {
      path: "/",
      name: ROUTE_NAME.HOME,
      // Club organizations land on the Schedule tab (MAI-4748); network
      // admins on Network Management; everyone else on the Feed.
      redirect: () => ({
        name: getDefaultLandingRouteName(
          useAuthStore().defaultOrg?.organization_type
        ),
      }),
    },
    {
      path: "/sentry-debug-caetheechoh9mo1faePa",
      name: ROUTE_NAME.SENTRY,
      component: SentryView,
    },
  ],
});

router.beforeEach(async (to, _from, next) => {
  const { isLoggedIn, canManagePayments } = useAuthStore();
  const impersonationStore = useImpersonationStore();

  const publicRouteNames = [
    ROUTE_NAME.LOGIN,
    ROUTE_NAME.LOGIN_PASSWORD,
    ROUTE_NAME.LOGIN_MAGIC_LINK,
    ROUTE_NAME.LOGOUT,
    ROUTE_NAME.FORGOT_PASSWORD,
    ROUTE_NAME.RESET_PASSWORD,
  ];

  const isMagicLinkPath = LOGIN_MAGIC_LINK_PATH.includes(to.path);
  const magicLinkToken = to.query.token;
  const isPublic =
    publicRouteNames.some((x) => x === to.name) || isMagicLinkPath;

  // Handle impersonation before routing to prevent API race conditions
  if (isLoggedIn && to.query.impersonateOrg && !isPublic) {
    const impersonateOrgId = to.query.impersonateOrg;

    if (typeof impersonateOrgId === "string") {
      // Check if we're already impersonating the correct organization
      const currentImpersonatedId =
        impersonationStore.impersonatedOrg?.id?.toString();
      const requestedOrgId = impersonateOrgId;

      if (
        !impersonationStore.isImpersonating ||
        currentImpersonatedId !== requestedOrgId
      ) {
        try {
          await impersonationStore.restoreImpersonationFromUrlById(
            impersonateOrgId
          );

          // Check if impersonation actually worked
          if (!impersonationStore.isImpersonating) {
            // Invalid impersonateOrg value, remove it from URL
            const cleanQuery = { ...to.query };
            delete cleanQuery.impersonateOrg;
            return next({ ...to, query: cleanQuery });
          }
        } catch (error) {
          // Invalid impersonateOrg value, remove it from URL
          const cleanQuery = { ...to.query };
          delete cleanQuery.impersonateOrg;
          return next({ ...to, query: cleanQuery });
        }
      }
    }
  }

  if (to.name == ROUTE_NAME.LOGOUT) {
    next();
  } else if (!isPublic && !isLoggedIn) {
    next({ name: ROUTE_NAME.LOGIN });
  } else if (to.meta.requiresPayments && !canManagePayments) {
    // Basic admins (MAI-4956): the menu hides these, deep links land on Schedule.
    next({ name: ROUTE_NAME.SCHEDULE });
  } else if (isPublic && isLoggedIn) {
    next({ name: ROUTE_NAME.HOME });
  } else if (isMagicLinkPath && magicLinkToken) {
    next({
      name: ROUTE_NAME.LOGIN_MAGIC_LINK,
      params: { token: magicLinkToken },
    });
    next({ name: ROUTE_NAME.LOGIN });
  } else {
    next();
  }
});

export default router;
